import { FC } from 'react';

import { AppDashboard } from '@shared/types/app';
import Markdown from 'markdown-to-jsx';

import { useAnalytics } from '@hooks/useAnalytics';
import { useDynamicAction } from '@hooks/useDynamicAction';

import { useRouter } from '@router/index';

import Button, { ButtonProps } from '@components/buttons/Button';
import { AdvancedPieChart } from '@components/charts/AdvancedPieChart';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import Card from '@components/layout/Card/Card';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';

import { SharedDashboardSectionProps } from '../DashboardScreen.types';
import { PrimaryEngagementCard } from './PrimaryEngagementCard';
import { TipJarSection } from './TipJarSection';

const WeeklyScoreAction: FC<{ action: AppDashboard['primaryCard']['actions'][number] }> = ({
  action,
}) => {
  const { track } = useAnalytics();
  const { handleDynamicAction } = useDynamicAction();

  return (
    <div
      key={action.label}
      className="py-1 px-2 text-align-center w-100"
      style={{ background: 'rgba(255, 255, 255, 0.1)', borderRadius: 8 }}
      onClick={() => {
        track('Weekly Score Action Clicked', action);

        handleDynamicAction(action, { state: { back: '/', forceBack: true } });
      }}
    >
      <div style={{ padding: '2px 0 0' }}>
        <img
          src={action.iconUrl}
          alt={action.label}
          style={{ width: 24, height: 24, padding: '1px 0', objectFit: 'contain' }}
        />
      </div>
      <div style={{ fontSize: '.6rem', lineHeight: 1 }}>{action.label}</div>
    </div>
  );
};

export const WeeklyScore: FC<SharedDashboardSectionProps> = ({
  isAppDashboardReady,
  appDashboard,
}) => {
  const { navigate } = useRouter();
  const { track } = useAnalytics();

  if (!isAppDashboardReady) {
    return (
      <>
        <div>
          <div className="mb-3">
            <Skeleton height={116} />
          </div>
        </div>
      </>
    );
  }

  if (appDashboard?.referralCard) {
    return <TipJarSection />;
  }

  if (appDashboard?.primaryEngagementCard) {
    return <PrimaryEngagementCard primaryEngagementCard={appDashboard.primaryEngagementCard} />;
  }

  if (!appDashboard?.primaryCard) {
    return <></>;
  }

  const { title, messages, chartData, actions, metricMessage } = appDashboard?.primaryCard;

  return (
    <>
      <div>
        <Card>
          <Card.Body>
            <div className="d-flex gap-2 align-items-center">
              <div style={{ width: 70, height: 60 }}>
                <AdvancedPieChart
                  showLabels
                  data={chartData}
                  labelFontSize={18}
                  labelPosition={0}
                  label={({ dataEntry }) =>
                    dataEntry.key === 'completed' ? `${dataEntry.value}%` : undefined
                  }
                />
              </div>
              <div>
                <div className="fs-5">
                  <strong>{title}</strong>
                </div>
                <Markdown className="opacity-75" options={{ forceWrapper: true }}>
                  {messages?.[0]}
                </Markdown>
              </div>
            </div>
            {metricMessage && (
              <div className="mt-2">
                <Button
                  roundness="rounded"
                  color={metricMessage.color as ButtonProps['color']}
                  variant={metricMessage.variant as ButtonProps['variant']}
                  onClick={() => {
                    track('Click Metrics Message');
                    navigate(`/manage/entertainer/metrics`);
                  }}
                  contentElement={
                    <span className="d-flex align-items-center justify-content-center">
                      <div
                        className={`btn_start-icon ${metricMessage.startIconAlign ? `align-self-${metricMessage.startIconAlign}` : ''}`}
                      >
                        <FontAwesomeIcon name={metricMessage.faIcon} />
                      </div>

                      <span>
                        <Markdown options={{ forceWrapper: true }}>{metricMessage.text}</Markdown>
                      </span>

                      <div className="ml-auto">
                        <div className="d-flex gap-2 align-items-center">
                          {metricMessage.ctaText && (
                            <div style={{ textWrap: 'nowrap' }}>
                              <Markdown>{metricMessage.ctaText}</Markdown>
                            </div>
                          )}
                          <FontAwesomeIcon name="chevron-right" />
                        </div>
                      </div>
                    </span>
                  }
                />
              </div>
            )}
            <div className="d-flex gap-2 mt-2">
              {actions.map((action) => (
                <WeeklyScoreAction key={action.label} action={action} />
              ))}
            </div>
          </Card.Body>
        </Card>
      </div>
    </>
  );
};
